<template>
  <b-container fluid>
    <b-row>
      <b-col
        cols="12"
        lg="8"
        class="text-center text-lg-left mb-2 mb-lg-0"
      >
        <b-form-radio-group
          v-model="pageSize"
          :options="pageSizeOptions"
          :disabled="loading"
        />
      </b-col>
      <b-col
        v-if="showSearchTerm"
        cols="12"
        lg="4"
      >
        <s-search-input
          v-model="searchTerm"
          :placeholder="searchPlaceholderHandler"
        />
      </b-col>
    </b-row>
    <b-row class="mt-4">
      <b-col>
        <slot />
      </b-col>
    </b-row>
    <b-row
      v-if="!loading"
      class="mb-2"
    >
      <b-col>
        <grid-paginator
          :total-rows="totalRows"
          :per-page="pageSize"
          v-model="pageNumber"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col class="text-center text-lg-right no-internal-print">
        <slot name="footer-buttons" />
        <b-button
          variant="primary"
          @click="onCloseClick"
        >
          {{ $t('actions.close') }}
        </b-button>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import merge from 'lodash/merge';
import isEqual from 'lodash/isEqual';
import GridPaginator from '@/components/lists/Paginator';

const DELAYED_INPUT_TIMER = 1000;

const pageSizes = [
  15,
  30,
  50,
  100,
];

/**
 * Painel de relatório paginado.
 *
 * Painel contendo controle de paginação, tamanho da pagina, termo de busca e slot
 * para conteúdo/listagem.
 *
 * Trabalha basicamente com v-model recebendo e devolvendo os dados de filtragem e paginação.
 *
 * O termo de busca (input text) trabalha com um delay de atualização para aguardar que o usuário
 * termine de digitar antes de disparar a alteração.
 */
export default {
  name: 'PageReportPanel',
  components: {
    GridPaginator,
  },
  props: {
    /**
     * Objeto de controle de paginação e filtro.
     *
     * O formato suportado é o mesmo usado pelo mapeador pagedRequestBuilder.
     *
     * Formato:
     * {
     *   page: Number,
     *   pageSize: Number,
     *   filter: {
     *     searchTerm: String
     *   }
     * }
     */
    value: {
      type: Object,
    },
    /**
     * Total de registros retornados.
     */
    totalRows: {
      type: Number,
    },
    /**
     * Informa se está havendo carregamento.
     */
    loading: {
      type: Boolean,
      default: false,
    },
    showSearchTerm: {
      type: Boolean,
      default: true,
    },
    /**
     * Nome da propriedade do termo de busca dentro de "filter".
     */
    searchTermProperty: {
      type: String,
      default: 'searchTerm',
    },
    searchPlaceholder: {
      type: String,
    },
    minSearchLength: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    rawSearchTerm: null,
    inputDelayTimer: null,
  }),
  methods: {
    emitInput(changes, delay = false) {
      if (delay) {
        this.doDelayedInput(changes);
        return;
      }
      const valueToEmit = merge({}, this.value, changes);
      if (!isEqual(valueToEmit, this.value)) {
        this.$emit('input', valueToEmit);
      }
    },
    doDelayedInput(changes) {
      if (this.inputDelayTimer !== null) {
        clearTimeout(this.inputDelayTimer);
      }
      this.inputDelayTimer = setTimeout(() => {
        this.inputDelayTimer = null;
        this.emitInput(changes);
      }, DELAYED_INPUT_TIMER);
    },
    onCloseClick() {
      this.$confirmMessage({ code: 'MC027' })
        .accepted(() => window.close());
    },
  },
  computed: {
    searchPlaceholderHandler() {
      return this.searchPlaceholder || this.$t('general.search');
    },
    pageSizeOptions() {
      return pageSizes.map(pageSize => ({
        value: pageSize,
        text: this.$t('general.x-per-page', {
          pageSize,
        }),
      }));
    },
    pageNumber: {
      get() {
        return this.value.page;
      },
      set(value) {
        this.emitInput({
          page: value,
        });
      },
    },
    pageSize: {
      get() {
        return this.value.pageSize;
      },
      set(value) {
        this.emitInput({
          page: 1,
          pageSize: value,
        });
      },
    },
    searchTerm: {
      get() {
        return this.rawSearchTerm;
      },
      set(value) {
        this.rawSearchTerm = value;
        const valueToSet = value && value.length >= this.minSearchLength ? value : null;

        this.emitInput({
          page: 1,
          filter: {
            [this.searchTermProperty]: valueToSet,
          },
        }, true);
      },
    },
  },
};
</script>
